import { useCallback, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowDown, ArrowUp, X } from "lucide-react";
import { Card } from "@/design-system/components/ui";
import { DataTable, type Column } from "@/components/layout/DataTable";
import { SavedFilterBar } from "@/components/ux/SavedFilterBar";
import { cn } from "@/design-system/lib/utils";
import { SPV_POOL_STATUSES, type SpvPoolStatus } from "@/types/spvPool";
import type { PoolInFlightDto } from "@/types/capitalMarketsDashboard";
import { PoolStatusBadge } from "../shared/components";
import { formatCurrency, formatPct, humanizeStatus } from "../shared/format";

/** Persisted filter state — serialized into a `SavedFilter` row. */
export interface PoolsInFlightFilter {
  stages: SpvPoolStatus[];
  hasBlockedGates: boolean;
}

const EMPTY_FILTER: PoolsInFlightFilter = { stages: [], hasBlockedGates: false };

type SortKey =
  | "poolName"
  | "daysInCurrentStatus"
  | "contributionPct"
  | "nextStageMissingGateCount";

export interface ConcentrationFilter {
  dimensionLabel: string;
  key: string;
}

interface Props {
  pools: PoolInFlightDto[];
  loading: boolean;
  error: boolean;
  /** Cross-filter set by clicking an Aggregate Concentration slice. */
  concentrationFilter: ConcentrationFilter | null;
  onClearConcentrationFilter: () => void;
}

/**
 * SPV Pool v1.0 §4.7 — the Pools In Flight table. Sortable columns, row
 * click-through to the pool detail, a saved-filter strip, and a
 * missing-gate chip per row.
 */
export function PoolsInFlightTable({
  pools,
  loading,
  error,
  concentrationFilter,
  onClearConcentrationFilter,
}: Props) {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<PoolsInFlightFilter>(EMPTY_FILTER);
  const [sortKey, setSortKey] = useState<SortKey>("poolName");
  const [sortAsc, setSortAsc] = useState(true);

  const applyFilter = useCallback((next: PoolsInFlightFilter) => {
    setFilter({
      stages: Array.isArray(next.stages) ? next.stages : [],
      hasBlockedGates: !!next.hasBlockedGates,
    });
  }, []);

  const toggleStage = (stage: SpvPoolStatus) => {
    setFilter((f) => ({
      ...f,
      stages: f.stages.includes(stage)
        ? f.stages.filter((s) => s !== stage)
        : [...f.stages, stage],
    }));
  };

  const toggleSort = (key: SortKey) => {
    if (key === sortKey) {
      setSortAsc((v) => !v);
    } else {
      setSortKey(key);
      setSortAsc(true);
    }
  };

  const rows = useMemo(() => {
    let next = pools;
    if (filter.stages.length > 0) {
      next = next.filter((p) => filter.stages.includes(p.currentStatus));
    }
    if (filter.hasBlockedGates) {
      next = next.filter((p) => p.nextStageMissingGateCount > 0);
    }
    const sorted = [...next].sort((a, b) => {
      let cmp = 0;
      if (sortKey === "poolName") {
        cmp = a.poolName.localeCompare(b.poolName);
      } else {
        cmp = (a[sortKey] as number) - (b[sortKey] as number);
      }
      return sortAsc ? cmp : -cmp;
    });
    return sorted;
  }, [pools, filter, sortKey, sortAsc]);

  const columns: Column<PoolInFlightDto>[] = useMemo(
    () => [
      {
        key: "poolName",
        header: "Pool Name",
        render: (p) => (
          <Link
            to={`/capital-markets/pools/${p.id}`}
            className="font-semibold hover:text-gold-shadow"
          >
            {p.poolName}
          </Link>
        ),
      },
      { key: "trust", header: "SPV Trust", render: (p) => p.spvTrustName },
      {
        key: "status",
        header: "Status",
        render: (p) => <PoolStatusBadge status={p.currentStatus} />,
      },
      {
        key: "days",
        header: "Days in Status",
        render: (p) => p.daysInCurrentStatus,
      },
      {
        key: "target",
        header: "Target Size",
        render: (p) => formatCurrency(p.targetPoolSize),
      },
      {
        key: "contribution",
        header: "Contribution",
        render: (p) => formatPct(p.contributionPct),
      },
      {
        key: "gates",
        header: "Next Stage",
        render: (p) => <GateChip count={p.nextStageMissingGateCount} />,
      },
      {
        key: "actions",
        header: "",
        className: "w-10 text-right",
        render: (p) => (
          <button
            type="button"
            onClick={() => navigate(`/capital-markets/pools/${p.id}`)}
            className="text-caption text-gold-shadow font-display uppercase tracking-wide hover:underline"
          >
            View
          </button>
        ),
      },
    ],
    [navigate],
  );

  return (
    <Card>
      <div className="flex items-center justify-between gap-space-3 mb-space-3 flex-wrap">
        <h2 className="font-display font-bold text-h3 m-0">Pools In Flight</h2>
        <SavedFilterBar<PoolsInFlightFilter>
          pageKey="capital-markets-pools-in-flight"
          currentState={filter}
          onApply={applyFilter}
        />
      </div>

      <div className="flex flex-wrap items-center gap-space-2 mb-space-3">
        {SPV_POOL_STATUSES.filter(
          (s) => s !== "Active" && s !== "WoundDown",
        ).map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => toggleStage(s)}
            className={cn(
              "px-space-2 py-[2px] rounded-full text-caption font-display uppercase tracking-wide border",
              filter.stages.includes(s)
                ? "bg-gold text-onyx border-gold font-semibold"
                : "bg-white-pure text-gray-mid border-border-light hover:text-onyx",
            )}
          >
            {humanizeStatus(s)}
          </button>
        ))}
        <button
          type="button"
          onClick={() =>
            setFilter((f) => ({ ...f, hasBlockedGates: !f.hasBlockedGates }))
          }
          className={cn(
            "px-space-2 py-[2px] rounded-full text-caption font-display uppercase tracking-wide border",
            filter.hasBlockedGates
              ? "bg-onyx text-gold border-onyx font-semibold"
              : "bg-white-pure text-gray-mid border-border-light hover:text-onyx",
          )}
        >
          Blocked on gates
        </button>
      </div>

      {concentrationFilter && (
        <div
          data-testid="concentration-filter-chip"
          className="flex items-center gap-space-2 mb-space-3 px-space-3 py-space-2 rounded-md bg-gold-highlight/15 text-small"
        >
          <span>
            Concentration view — {concentrationFilter.dimensionLabel}:{" "}
            <span className="font-semibold">{concentrationFilter.key}</span>
          </span>
          <button
            type="button"
            onClick={onClearConcentrationFilter}
            aria-label="Clear concentration filter"
            className="text-gray-mid hover:text-onyx"
          >
            <X size={14} strokeWidth={1.5} aria-hidden />
          </button>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-space-3 mb-space-2">
        <SortButton
          label="Name"
          active={sortKey === "poolName"}
          asc={sortAsc}
          onClick={() => toggleSort("poolName")}
        />
        <SortButton
          label="Days in Status"
          active={sortKey === "daysInCurrentStatus"}
          asc={sortAsc}
          onClick={() => toggleSort("daysInCurrentStatus")}
        />
        <SortButton
          label="Contribution"
          active={sortKey === "contributionPct"}
          asc={sortAsc}
          onClick={() => toggleSort("contributionPct")}
        />
        <SortButton
          label="Open Gates"
          active={sortKey === "nextStageMissingGateCount"}
          asc={sortAsc}
          onClick={() => toggleSort("nextStageMissingGateCount")}
        />
      </div>

      <DataTable
        columns={columns}
        rows={rows}
        loading={loading}
        error={error ? "Could not load pools in flight." : null}
        emptyMessage="No pools in flight — every pool is Active or wound down."
        rowKey={(p) => p.id}
      />
    </Card>
  );
}

/** Missing-gate chip — onyx-on-gold when blocked, gold-on-onyx when ready. */
export function GateChip({ count }: { count: number }) {
  if (count <= 0) {
    return (
      <span className="inline-flex items-center px-space-2 py-[2px] rounded-sm bg-onyx text-gold font-display text-[10px] font-bold uppercase tracking-[0.16em]">
        Ready to advance
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-space-2 py-[2px] rounded-sm bg-gold text-onyx font-display text-[10px] font-bold uppercase tracking-[0.16em]">
      {count} {count === 1 ? "gate" : "gates"} open
    </span>
  );
}

function SortButton({
  label,
  active,
  asc,
  onClick,
}: {
  label: string;
  active: boolean;
  asc: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "inline-flex items-center gap-space-1 text-caption font-display uppercase tracking-wide",
        active ? "text-onyx font-semibold" : "text-gray-mid hover:text-onyx",
      )}
    >
      {label}
      {active &&
        (asc ? (
          <ArrowUp size={12} strokeWidth={2} aria-hidden />
        ) : (
          <ArrowDown size={12} strokeWidth={2} aria-hidden />
        ))}
    </button>
  );
}
